import { useQuery } from '@tanstack/react-query';
import { isLyrraOne } from '@/lib/platform';
import { fetchLoPlanUsage } from '@/lib/lyrraone-hub';
import {
  hasLoPlanFeature,
  tierMeetsLyrraOneFeature,
  type LoPlanUsage,
  type LyrraOnePlanFeature,
  type LyrraOnePlanTier,
} from '@/lib/lyrraone-plan';

export function useLyrraOnePlan() {
  const enabled = isLyrraOne();

  const query = useQuery({
    queryKey: ['lyrraone', 'plan-usage'],
    queryFn: fetchLoPlanUsage,
    enabled,
    staleTime: 2 * 60 * 1000,
  });

  const plan = (query.data?.plan ?? 'discovery') as LyrraOnePlanTier;
  const data = query.data as LoPlanUsage | undefined;

  return {
    isLyrraOne: enabled,
    loading: enabled && query.isLoading,
    error: query.error,
    refetch: query.refetch,
    plan,
    usage: data?.usage,
    features: data?.features,
    planUsage: data ?? null,
    hasFeature(feature: LyrraOnePlanFeature): boolean {
      if (!enabled) return true;
      if (data?.features) return hasLoPlanFeature(data, feature);
      return tierMeetsLyrraOneFeature(plan, feature);
    },
    meetsMinTier(minTier: LyrraOnePlanTier): boolean {
      if (!enabled) return true;
      const ranks = { discovery: 0, creator: 1, expert: 2 } as const;
      return ranks[plan] >= ranks[minTier];
    },
    quota(key: string): {
      used: number;
      limit: number;
      unlimited: boolean;
      atLimit: boolean;
      canAdd: boolean;
    } {
      const item = data?.usage[key];
      if (!item) {
        return { used: 0, limit: -1, unlimited: true, atLimit: false, canAdd: true };
      }
      const unlimited = item.limit < 0;
      const atLimit = !unlimited && item.used >= item.limit;
      return {
        used: item.used,
        limit: item.limit,
        unlimited,
        atLimit,
        canAdd: unlimited || item.used < item.limit,
      };
    },
  };
}
